import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../../../Common/Modal/Modal';
import { InputText } from 'primereact/inputtext';
import Toggle from '@Components/Common/Toggle/Toggle';
import { useSelector } from 'react-redux';
import ColorChooser from '@Components/Common/ColorChooser';
import Tag from '@Components/Common/Tag/Tag';

interface TaskTag {
	id: number;
	name: string;
	isActive: boolean;
	subscriptionId: number;
	backgroundColor: string;
	fontColor: string;
}

interface AddJobTitleProps {
	onSave: (department: TaskTag) => void;
	onCancel: () => void;
}

const AddTaskTag: React.FC<AddJobTitleProps> = ({ onSave, onCancel }) => {
	// Memoize subscription ID to prevent unnecessary re-renders
	const currentSubscriptionId = useSelector((state: { user: { currentSubscriptionId: number } }) => state.user.currentSubscriptionId);

	const initialTaskTag = useMemo(
		() => ({
			id: 0,
			name: '',
			isActive: true,
			backgroundColor: '#5F0DF0',
			fontColor: '#509823',
			subscriptionId: currentSubscriptionId || 0,
		}),
		[currentSubscriptionId]
	);

	const [newTaskTag, setNewTaskTag] = useState<TaskTag>(initialTaskTag);
	const isInputInvalid = !newTaskTag.name.trim(); // Dynamically determine invalid state

	// Memoized change handlers
	const handleOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setNewTaskTag((prev) => (prev.name === value ? prev : { ...prev, name: value }));
	}, []);

	const handleToggleChange = useCallback((e: { target: { checked: boolean } }) => {
		setNewTaskTag((prev) => (prev.isActive === e.target.checked ? prev : { ...prev, isActive: e.target.checked }));
	}, []);

	const onBackgroundColorChange = useCallback((color: string) => {
		setNewTaskTag((prev) => ({ ...prev, backgroundColor: color }));
	}, []);

	const onFontColorChange = useCallback((color: string) => {
		setNewTaskTag((prev) => ({ ...prev, fontColor: color }));
	}, []);

	const handleSave = useCallback(() => {
		if (isInputInvalid) return;
		onSave(newTaskTag);
	}, [isInputInvalid, onSave, newTaskTag]);

	return (
		<Modal
			title='Add Task Tag'
			body={
				<>
					<InputText
						id='name'
						name='name'
						placeholder='Name'
						invalid={isInputInvalid}
						type='text'
						value={newTaskTag.name}
						onChange={handleOnChange}
						maxLength={175}
						className='mb-3'
					/>
					<Toggle name='isActive' text='Active' checked={newTaskTag.isActive} onChange={handleToggleChange} />
					<ColorChooser
						name='backgroundColor'
						title='Background Color'
						selectedColor={newTaskTag.backgroundColor}
						onChange={onBackgroundColorChange}
					/>
					<ColorChooser name='fontColor' title='Font Color' selectedColor={newTaskTag.fontColor} onChange={onFontColorChange} />
					<div className='mt-5'>
						<span className='me-2'>Preview:</span>
						<Tag title={newTaskTag.name} backgroundColor={newTaskTag.backgroundColor} color={newTaskTag.fontColor} />
					</div>
				</>
			}
			primaryButtonText='Add'
			onPrimaryClick={handleSave}
			onSecondaryClick={onCancel}
			onCloseClick={onCancel}
		/>
	);
};

AddTaskTag.propTypes = {
	onSave: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};

export default AddTaskTag;
